import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { tokens } from "../../../../theme";
import patternUrl from "../../../../assets/pattern.png";
import { RootState } from "../../../../store/Index";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";

interface BookingLayoutProps {
  stepperComponent: React.ReactNode;
  formContentComponent: React.ReactNode;
  sidebarContentComponent: React.ReactNode;
  onNext: () => void;
  onBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  submitting?: boolean;
  isEdit?: boolean;
  onUpdate?: () => void;
  sidebarTitle?: string;
  submitLabel?: string;
}

const BookingLayout: React.FC<BookingLayoutProps> = ({
  stepperComponent,
  formContentComponent,
  sidebarContentComponent,
  onNext,
  onBack,
  isFirstStep,
  isLastStep,
  submitting = false,
  isEdit = false,
  sidebarTitle = "Booking Information",
  submitLabel = "Submit Booking",
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const isDarkMode = theme.palette.mode === "dark";

  const color400 = colors.primary[400] || "#724E41";
  const color10 = colors.primary[10] || "#C29363";
  const primaryGradient = `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`;

  const headerGradient = isDarkMode
    ? `linear-gradient(135deg, ${colors.primary[900]}F2 0%, ${colors.primary[800]}E6 100%), url(${patternUrl})`
    : `linear-gradient(135deg, ${color400}F2 0%, ${color10}E6 50%, ${color400}F2 100%), url(${patternUrl})`;

  const paperSx = {
    borderRadius: 4,
    overflow: "hidden",
    backgroundColor: isDarkMode ? colors.primary[900] : "#fff",
    border: `1px solid ${colors.primary[300]}15`,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  } as const;

  const headerSx = {
    backgroundImage: headerGradient,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundBlendMode: "multiply",
    backgroundColor: isDarkMode ? colors.primary[900] : colors.primary[800],
    p: 3,
    height: 125,
    boxSizing: "border-box",
    flexShrink: 0,
  } as const;

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.8fr 1fr" },
          gap: 3,
          width: "100%",
          maxWidth: "1400px",
          alignItems: "stretch",
        }}
      >
        {/* ── Left panel ── */}
        <Paper elevation={0} sx={paperSx}>
          {/* Header: stepper only */}
          <Box
            sx={{
              ...headerSx,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ width: "100%" }}>{stepperComponent}</Box>
          </Box>

          {/* Form content + sticky nav */}
          <Box
            sx={{
              p: { xs: 3, md: 4, lg: 5 },
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Scrollable step content */}
            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {formContentComponent}
            </Box>

            {/* Sticky nav bar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pt: 2,
                mt: 2,
                borderTop: `1px solid ${isDarkMode ? colors.primary[700] : "#E2E8F0"}`,
                flexShrink: 0,
              }}
            >
              <Button
                onClick={onBack}
                disabled={isFirstStep || submitting}
                startIcon={<KeyboardArrowLeft />}
                sx={{
                  border: "1px solid",
                  borderColor: isDarkMode
                    ? `${colors.primary[300]}50`
                    : "#CBD5E0",
                  color: isDarkMode ? colors.primary[200] : "#4A5568",
                  fontWeight: 600,
                  fontSize: "14px",
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 4,
                  py: 1.2,
                  "&:hover": { opacity: 0.9, backgroundColor: "transparent" },
                  "&.Mui-disabled": { opacity: 0.3 },
                }}
              >
                Back
              </Button>

              <Button
                onClick={onNext}
                disabled={submitting}
                endIcon={isLastStep ? undefined : <KeyboardArrowRight />}
                startIcon={
                  isLastStep && submitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
                sx={{
                  background: primaryGradient,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "14px",
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 5,
                  py: 1.2,
                  boxShadow: isDarkMode ? "none" : `0px 4px 12px ${color400}33`,
                  "&:hover": { opacity: 0.9 },
                  "&.Mui-disabled": { opacity: 0.6 },
                }}
              >
                {isLastStep
                  ? submitting
                    ? isEdit
                      ? "Updating..."
                      : "Submitting..."
                    : isEdit
                      ? "Update"
                      : submitLabel
                  : "Next"}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* ── Right panel (preview sidebar) ── */}
        <Paper elevation={0} sx={paperSx}>
          <Box
            sx={{
              ...headerSx,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: isDarkMode ? colors.primary[700] : "#fff",
                fontWeight: 600,
                letterSpacing: "0.3px",
              }}
            >
              {sidebarTitle}
            </Typography>
            <Box
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: "20px",
                border: isDarkMode
                  ? `1px solid ${colors.primary[300]}40`
                  : "1px solid rgba(255,255,255,0.4)",
                color: isDarkMode ? colors.primary[300] : "#fff",
                fontSize: "12px",
                fontWeight: 500,
                backgroundColor: isDarkMode
                  ? "transparent"
                  : "rgba(255,255,255,0.08)",
              }}
            >
              {isEdit ? "Editing" : "Preview"}
            </Box>
          </Box>

          <Box
            sx={{
              p: 3,
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
            }}
          >
            {sidebarContentComponent}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default BookingLayout;
